import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Download, BookOpen, Target, Zap, BarChart3, Layers } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const blueprintContents = [
  {
    icon: Target,
    title: "AI-First GTM Strategy Framework",
    description: "Complete framework for building go-to-market strategies that leverage AI at every stage",
  },
  {
    icon: Layers,
    title: "Channel Prioritization Matrix",
    description: "Data-driven approach to choosing and prioritizing your marketing channels",
  },
  {
    icon: Zap,
    title: "Content Velocity Playbook",
    description: "How to 10x your content production with AI while maintaining quality",
  },
  {
    icon: BarChart3,
    title: "AI Tool Stack Recommendations",
    description: "Curated list of AI tools for each GTM function with implementation guides",
  },
];

const whoThisIsFor = [
  "SaaS founders planning their GTM strategy",
  "Marketing leaders building AI-first teams",
  "Growth teams scaling beyond founder-led sales",
  "Companies transitioning to product-led growth",
];

const testimonials = [
  {
    quote: "This blueprint saved us months of trial and error. The AI tool stack alone is worth it.",
    author: "SaaS Founder, Series A",
  },
  {
    quote: "Finally, a GTM framework built for 2024+, not recycled 2015 playbooks.",
    author: "VP Marketing, B2B SaaS",
  },
];

export default function SaaSGTMBlueprint() {
  return (
    <Layout>
      <SEO 
        title="SaaS GTM Blueprint (AI-First Edition) | Free Download"
        description="Free AI-first go-to-market blueprint for SaaS companies. Complete GTM framework, channel prioritization matrix, content playbook, and AI tool stack."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="container-wide relative z-10">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Resources", href: "/resources" },
              { label: "SaaS GTM Blueprint" },
            ]}
            className="mb-8"
          />
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <Badge className="mb-4 bg-blue-500/10 text-blue-500 hover:bg-blue-500/20">
                <Download className="w-3 h-3 mr-1" />
                Free Download
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                The SaaS GTM Blueprint <span className="text-gradient">(AI-First Edition)</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                The complete go-to-market framework for SaaS companies in the AI era. 
                Strategy, channels, content, and tools—all in one actionable guide.
              </p>

              <div className="space-y-4 mb-8">
                <h3 className="font-semibold">Who This Is For:</h3>
                <ul className="space-y-2">
                  {whoThisIsFor.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button 
                size="lg" 
                className="rounded-full"
                data-cal-link="thecloudseo/gtm-blueprint"
                data-cal-namespace="gtm-blueprint"
                data-cal-config='{"layout":"month_view"}'
              >
                Get the Blueprint
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>

              <p className="text-sm text-muted-foreground mt-4">
                Free download. No email required—just book a quick call.
              </p>
            </AnimatedSection>

            {/* What's Inside */}
            <AnimatedSection delay={0.2}>
              <div className="bg-card rounded-3xl border border-border p-8">
                <div className="flex items-center gap-2 mb-6">
                  <BookOpen className="w-6 h-6 text-primary" />
                  <h2 className="text-2xl font-bold">What's Inside</h2>
                </div>
                <div className="space-y-6">
                  {blueprintContents.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex gap-4"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <item.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 border-t border-border">
        <div className="container-wide">
          <AnimatedSection>
            <h2 className="text-2xl font-bold text-center mb-12">What People Are Saying</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 rounded-2xl bg-card border border-border"
                >
                  <p className="text-lg italic mb-4">"{testimonial.quote}"</p>
                  <p className="text-sm text-muted-foreground">— {testimonial.author}</p>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 border-t border-border">
        <div className="container-wide">
          <AnimatedSection>
            <h2 className="text-2xl font-bold text-center mb-4">Need Help Implementing?</h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              The blueprint gives you the strategy. We can help you execute it.
            </p>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                { title: "SaaS SEO", slug: "/services/geo/saas-seo", description: "Organic growth for software companies" },
                { title: "AI Content", slug: "/services/ai-content/ai-content-creation", description: "10x your content velocity" },
                { title: "AI Agents", slug: "/services/ai-agents/lead-qualification", description: "Automate lead qualification" },
              ].map((service, index) => (
                <Link
                  key={index}
                  to={service.slug}
                  className="group p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all"
                >
                  <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">{service.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{service.description}</p>
                  <span className="text-sm font-medium text-primary flex items-center">
                    Learn more <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-secondary/30">
        <div className="container-wide">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Build Your AI-First GTM Strategy
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Get the complete blueprint and start building a go-to-market strategy 
              that leverages AI at every stage.
            </p>
            <Button 
              size="lg" 
              className="rounded-full"
              data-cal-link="thecloudseo/gtm-blueprint"
              data-cal-namespace="gtm-blueprint"
              data-cal-config='{"layout":"month_view"}'
            >
              Get the Blueprint
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
